/**
 * FinancialPnLStatement.js
 * P&L Statement tab for the Financial Dashboard.
 * Fetches from /fin-pnl/pnl and /fin-pnl/years using API_BASE.
 */
import React, { useEffect, useState } from "react";
import api from "./apiClient";
import { useTheme, getT, glassCard } from "./ThemeContext";
import { useAppConfig } from "./AppContext";
import ExcelDownloadButton from "./components/ExcelDownloadButton";

const fmt = (n) =>
  n != null && n !== 0
    ? Number(n).toLocaleString("en-US", { style: "currency", currency: "USD" })
    : "—";

// Matches the same App Settings date_format convention used elsewhere (e.g. StudentMaster.js)
function fmtDate(iso, dfmt) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (dfmt === "DD/MM/YYYY") return `${d}/${m}/${y}`;
  if (dfmt === "YYYY-MM-DD") return `${y}-${m}-${d}`;
  return `${m}/${d}/${y}`; // MM/DD/YYYY default
}

const GROUP_ORDER = ["Income", "Expense", "Equity"];

const MONTH_NAME_TO_NUM = {
  Jan: 1, Feb: 2, Mar: 3, Apr: 4,  May: 5,  Jun: 6,
  Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
};

function parseMonthLabel(label) {
  // "Jan 2025" -> { month: 1, year: 2025 }
  const [mo, yr] = label.split(" ");
  return { month: MONTH_NAME_TO_NUM[mo], year: Number(yr) };
}

function Spinner({ T }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 220 }}>
      <span style={{
        display: "inline-block", width: 36, height: 36, borderRadius: "50%",
        border: "2px solid " + T.divider,
        borderTopColor: T.yellow,
        animation: "fin-spin 0.7s linear infinite",
      }} />
      <style>{`@keyframes fin-spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function TransactionDrillDown({ filter, onClose, T, gc }) {
  const { config } = useAppConfig();
  const dateFmt = config?.date_format || "MM/DD/YYYY";
  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [sortKey, setSortKey] = useState("transaction_date");
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"
  const PAGE = 100;

  const fetchPage = (offset) => {
    setLoading(true);
    api.get("/fin-remap/transactions", { params: { ...filter.params, limit: PAGE, offset } })
      .then(r => {
        setRows(prev => offset === 0 ? r.data.rows : [...prev, ...r.data.rows]);
        setTotal(r.data.total);
        setError("");
      })
      .catch(e => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPage(0); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const sum = rows.reduce((s, r) => s + Math.abs(Number(r.amount) || 0), 0);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const sortedRows = [...rows].sort((a, b) => {
    let av = a[sortKey], bv = b[sortKey];
    if (sortKey === "amount") { av = Math.abs(Number(av) || 0); bv = Math.abs(Number(bv) || 0); }
    else { av = (av || "").toString().toLowerCase(); bv = (bv || "").toString().toLowerCase(); }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const SortArrow = ({ col }) => (
    <span style={{ marginLeft: 4, opacity: sortKey === col ? 1 : 0.25, fontSize: 9 }}>
      {sortKey === col && sortDir === "desc" ? "▼" : "▲"}
    </span>
  );
  const thSortable = {
    padding: "9px 14px", textAlign: "left", fontSize: 9.5, fontWeight: 800,
    textTransform: "uppercase", letterSpacing: "0.08em", color: T.textMuted,
    borderBottom: "1px solid " + T.divider, cursor: "pointer", userSelect: "none",
    whiteSpace: "nowrap",
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        ...gc, width: "min(820px, 100%)", maxHeight: "82vh", display: "flex", flexDirection: "column",
        borderRadius: 16, overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between",
          borderBottom: "1px solid " + T.divider,
          background: T.isDark ? "rgba(247,206,11,0.06)" : "rgba(247,206,11,0.08)",
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 900, color: T.textPrimary }}>{filter.title}</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{filter.subtitle}</div>
          </div>
          <button onClick={onClose} style={{
            border: "none", background: "transparent", cursor: "pointer",
            fontSize: 18, color: T.textMuted, lineHeight: 1, padding: 4,
          }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          {error && (
            <div style={{ padding: 20, fontSize: 12, color: T.red }}>{error}</div>
          )}
          {loading && rows.length === 0 ? <Spinner T={T} /> : rows.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: T.textMuted, fontSize: 12 }}>
              No transactions found for this selection.
            </div>
          ) : (
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ position: "sticky", top: 0, background: T.isDark ? "rgba(4,15,32,0.98)" : "rgba(240,246,255,0.99)" }}>
                  <th onClick={() => toggleSort("transaction_date")} style={{ ...thSortable, padding: "9px 20px" }}>Date<SortArrow col="transaction_date"/></th>
                  <th onClick={() => toggleSort("description")} style={thSortable}>Description<SortArrow col="description"/></th>
                  <th onClick={() => toggleSort("category")} style={thSortable}>Category<SortArrow col="category"/></th>
                  <th onClick={() => toggleSort("bank_source")} style={thSortable}>Bank<SortArrow col="bank_source"/></th>
                  <th onClick={() => toggleSort("amount")} style={{ ...thSortable, textAlign: "right", padding: "9px 20px" }}>Amount<SortArrow col="amount"/></th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(r => (
                  <tr key={r.id} style={{ borderBottom: "1px solid " + T.divider }}>
                    <td style={{ padding: "8px 20px", color: T.textSub, whiteSpace: "nowrap" }}>{fmtDate(r.transaction_date, dateFmt)}</td>
                    <td style={{ padding: "8px 14px", color: T.textPrimary, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.description}>{r.description}</td>
                    <td style={{ padding: "8px 14px", color: T.textMuted, whiteSpace: "nowrap" }}>{r.category}</td>
                    <td style={{ padding: "8px 14px", color: T.textMuted, whiteSpace: "nowrap" }}>{r.bank_source || "—"}</td>
                    <td style={{ padding: "8px 20px", textAlign: "right", fontWeight: 700, color: T.textPrimary, fontVariantNumeric: "tabular-nums" }}>{fmt(Math.abs(r.amount))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "12px 22px", display: "flex", alignItems: "center", justifyContent: "space-between",
          borderTop: "1px solid " + T.divider, background: T.glass,
        }}>
          <div style={{ fontSize: 11, color: T.textMuted }}>
            Showing {rows.length} of {total} transaction{total === 1 ? "" : "s"}
            {rows.length > 0 && <> · Sum {fmt(sum)}</>}
          </div>
          {rows.length < total && (
            <button onClick={() => fetchPage(rows.length)} disabled={loading} style={{
              padding: "6px 14px", borderRadius: 8, border: "1px solid " + T.divider,
              background: "transparent", color: T.textPrimary, fontSize: 11, fontWeight: 700,
              cursor: loading ? "default" : "pointer",
            }}>{loading ? "Loading…" : "Load more"}</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FinancialPnLStatement({ yearProp, selectedMonths }) {
  const { isDark } = useTheme();
  const T  = getT(isDark);
  const gc = glassCard(T);

  const [data,    setData]    = useState({ months: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [drill,   setDrill]   = useState(null);

  useEffect(() => {
    setLoading(true);
    setError("");
    const params = {};
    if (yearProp) params.year = yearProp;
    if (selectedMonths && selectedMonths.length > 0 && selectedMonths.length < 12) {
      params.months = selectedMonths.join(",");
    }
    api.get("/fin-pnl/pnl", { params })
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [yearProp, selectedMonths]);

  const { months, rows } = data;
  const grouped = {};
  GROUP_ORDER.forEach(g => { grouped[g] = (rows || []).filter(r => r.income_expense === g); });

  const subtotal   = (g, m) => (grouped[g] || []).reduce((s, r) => s + (r.monthly[m] || 0), 0);
  const grandTotal = (g)    => (grouped[g] || []).reduce((s, r) => s + (r.total || 0), 0);
  const netRow     = (m)    => subtotal("Income", m) - subtotal("Expense", m);
  const netTotal   = ()     => grandTotal("Income")  - grandTotal("Expense");

  const thStyle = {
    padding: "12px 14px", fontWeight: 800,
    fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.10em",
    color: T.textMuted, borderBottom: "1px solid " + T.divider,
  };

  const rowBg    = T.glass;
  const hdrAcct  = T.accentText;
  const blueAcct = T.isDark ? "#60a5fa" : "#0369a1";

  const openCategoryMonth = (row, m) => {
    const { month, year } = parseMonthLabel(m);
    setDrill({
      title: row.category,
      subtitle: `${row.income_expense} · ${m}`,
      params: { year, month, category: row.category, income_expense: row.income_expense },
    });
  };
  const openCategoryTotal = (row) => {
    setDrill({
      title: row.category,
      subtitle: `${row.income_expense} · ${yearProp ? "Year " + yearProp : "All time"}`,
      params: { ...(yearProp ? { year: yearProp } : {}), category: row.category, income_expense: row.income_expense },
    });
  };
  const openGroupMonth = (group, m) => {
    const { month, year } = parseMonthLabel(m);
    setDrill({
      title: `Total ${group}`,
      subtitle: m,
      params: { year, month, income_expense: group },
    });
  };
  const openGroupTotal = (group) => {
    setDrill({
      title: `Total ${group}`,
      subtitle: yearProp ? `Year ${yearProp}` : "All time",
      params: { ...(yearProp ? { year: yearProp } : {}), income_expense: group },
    });
  };

  const clickableCell = { cursor: "pointer" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: T.textPrimary }}>
            Profit &amp; Loss Statement
          </h2>
          {yearProp && (
            <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 700 }}>
              {selectedMonths && selectedMonths.length < 12
                ? `${selectedMonths.join(", ")} · ${yearProp}`
                : `Full Year · ${yearProp}`}
            </span>
          )}
        </div>
        {rows && rows.length > 0 && (
          <ExcelDownloadButton
            endpoint="/fin-pnl/pnl/export"
            params={{
              year: yearProp,
              months: selectedMonths && selectedMonths.length > 0 && selectedMonths.length < 12
                ? selectedMonths.join(",") : undefined,
            }}
            filename={`PnL_Statement_${yearProp || "AllYears"}.xlsx`}
            label="Download Excel"
          />
        )}
      </div>

      {error && (
        <div style={{
          padding: "10px 14px", borderRadius: 10, fontSize: 12, fontWeight: 600,
          color: T.red, background: T.redBg, border: "1px solid " + T.red + "44",
        }}>{error}</div>
      )}

      {loading ? <Spinner T={T} /> : (!rows || rows.length === 0) ? (
        <div style={{ ...gc, padding: 56, textAlign: "center", color: T.textMuted }}>
          No data available. Upload a bank statement first.
        </div>
      ) : (
        <div style={{ ...gc, borderRadius: 16, overflowX: "auto" }}>
          <table style={{ minWidth: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{
                  ...thStyle, textAlign: "left", width: 220,
                  position: "sticky", left: 0,
                  background: T.isDark ? "rgba(4,15,32,0.96)" : "rgba(240,246,255,0.98)",
                  backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
                }}>Category</th>
                {months.map(m => (
                  <th key={m} style={{ ...thStyle, textAlign: "right", whiteSpace: "nowrap" }}>{m}</th>
                ))}
                <th style={{ ...thStyle, textAlign: "right", paddingRight: 20 }}>Total</th>
              </tr>
            </thead>

            <tbody>
              {GROUP_ORDER.map(group => {
                if (!grouped[group] || !grouped[group].length) return null;
                return (
                  <React.Fragment key={group}>
                    {/* Group header */}
                    <tr style={{ background: T.isDark ? "rgba(247,206,11,0.06)" : "rgba(247,206,11,0.08)", borderBottom: "1px solid " + T.divider }}>
                      <td colSpan={months.length + 2} style={{
                        padding: "7px 20px", fontWeight: 900, color: hdrAcct,
                        fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.14em",
                      }}>
                        {group}
                      </td>
                    </tr>

                    {/* Category rows */}
                    {grouped[group].map(row => (
                      <tr key={row.category}
                        style={{ borderBottom: "1px solid " + T.divider, transition: "background 0.1s" }}
                        onMouseEnter={e => e.currentTarget.style.background = T.glass}
                        onMouseLeave={e => e.currentTarget.style.background = ""}>
                        <td style={{
                          padding: "9px 20px 9px 36px", color: T.textSub,
                          position: "sticky", left: 0,
                          background: T.isDark ? "rgba(4,15,32,0.96)" : "rgba(240,246,255,0.98)",
                          backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
                        }}>{row.category}</td>
                        {months.map(m => {
                          const has = !!row.monthly[m];
                          return (
                            <td key={m} onClick={has ? () => openCategoryMonth(row, m) : undefined}
                              title={has ? "Click to view transactions" : undefined}
                              style={{
                                padding: "9px 14px", textAlign: "right",
                                color: row.income_expense === "Expense" ? hdrAcct : T.textSub,
                                fontVariantNumeric: "tabular-nums",
                                textDecoration: has ? "underline" : "none",
                                textDecorationColor: has ? (T.isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.14)") : "transparent",
                                textUnderlineOffset: "3px",
                                ...(has ? clickableCell : {}),
                              }}>
                              {has ? fmt(row.monthly[m]) : "—"}
                            </td>
                          );
                        })}
                        <td onClick={() => openCategoryTotal(row)} title="Click to view transactions" style={{
                          padding: "9px 20px", textAlign: "right",
                          fontWeight: 700, color: T.textPrimary,
                          fontVariantNumeric: "tabular-nums",
                          ...clickableCell,
                        }}>
                          {fmt(row.total)}
                        </td>
                      </tr>
                    ))}

                    {/* Subtotal row */}
                    <tr style={{ background: T.glass, borderTop: "1px solid " + T.divider, borderBottom: "1px solid " + T.divider }}>
                      <td style={{
                        padding: "9px 20px", fontWeight: 800, color: T.textPrimary,
                        position: "sticky", left: 0,
                        background: T.isDark ? "rgba(4,15,32,0.98)" : "rgba(240,246,255,0.99)",
                        backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
                      }}>Total {group}</td>
                      {months.map(m => (
                        <td key={m} onClick={() => openGroupMonth(group, m)} title="Click to view transactions" style={{
                          padding: "9px 14px", textAlign: "right", fontWeight: 800,
                          color: group === "Expense" ? hdrAcct : blueAcct,
                          fontVariantNumeric: "tabular-nums",
                          ...clickableCell,
                        }}>
                          {fmt(subtotal(group, m))}
                        </td>
                      ))}
                      <td onClick={() => openGroupTotal(group)} title="Click to view transactions" style={{
                        padding: "9px 20px", textAlign: "right",
                        fontWeight: 800, color: T.textPrimary,
                        fontVariantNumeric: "tabular-nums",
                        ...clickableCell,
                      }}>
                        {fmt(grandTotal(group))}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}

              {/* Net Profit / Loss */}
              <tr style={{ background: T.isDark ? "rgba(247,206,11,0.08)" : "rgba(247,206,11,0.10)", borderTop: "1px solid " + T.yellow + "44" }}>
                <td style={{
                  padding: "13px 20px", fontWeight: 900, color: hdrAcct, fontSize: 13,
                  position: "sticky", left: 0,
                  background: T.isDark ? "rgba(4,15,32,0.98)" : "rgba(240,246,255,0.99)",
                  backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
                }}>Net Profit / Loss</td>
                {months.map(m => {
                  const net = netRow(m);
                  return (
                    <td key={m} style={{
                      padding: "13px 14px", textAlign: "right", fontWeight: 800, fontSize: 13,
                      color: net >= 0 ? hdrAcct : T.textSub,
                      fontVariantNumeric: "tabular-nums",
                    }}>
                      {fmt(net)}
                    </td>
                  );
                })}
                <td style={{
                  padding: "13px 20px", textAlign: "right", fontWeight: 800, fontSize: 13,
                  color: netTotal() >= 0 ? hdrAcct : T.textSub,
                  fontVariantNumeric: "tabular-nums",
                }}>
                  {fmt(netTotal())}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {drill && (
        <TransactionDrillDown filter={drill} onClose={() => setDrill(null)} T={T} gc={gc} />
      )}
    </div>
  );
}
